import React, { useRef, useState } from 'react';
import { Drawer, Modal, Typography } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faImage, faPen, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../../context/ThemeContext';
import Table from '../../commonComponents/Table';
import TablePagination from '../../commonComponents/Pagination';
import CustomInput from '../../commonComponents/input';
import { ConfigProvider } from 'antd';
import { useItemsQuery } from './hooks/useItemsQuery';
import { useDeleteItem, useToggleSoldOut } from './hooks/useItemMutations';
import { Item, ItemsProps } from './types';
import { SEARCH_DEBOUNCE_MS } from './constants';
import { getAntdThemeConfig } from '../../utils/antdTheme';
import {
  ActionsCell,
  ActionIconButton,
  AddButton,
  BadgeOverlay,
  DescriptionText,
  DrawerContentWrapper,
  ItemEyeBadge,
  ItemNameCell,
  ItemNameText,
  ItemThumbnailWrap,
  NoImagePlaceholder,
  PageWrapper,
  PreviewBadgeOverlay,
  PreviewImageWrapper,
  PreviewImg,
  StatusTag,
  SoldOutSwitch,
  SoldOutSwitchWrap,
  TableWrapper,
  ThumbnailImg,
} from './styles';
import ItemFormModal from './ItemFormModal';
import { Tooltip } from 'antd';

const { Text } = Typography;

const Items: React.FC<ItemsProps> = ({ role }) => {
  const { isDarkMode } = useTheme();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Item | null>(null);
  const [deleteTargetId, setDeleteTargetId] = useState<number | string | null>(null);
  const [togglingId, setTogglingId] = useState<number | string | null>(null);
  const [previewItem, setPreviewItem] = useState<Item | null>(null);

  const { data, isLoading, isFetching } = useItemsQuery(debouncedSearch, currentPage, pageSize);
  const { mutate: deleteMutate, isPending: isDeleting } = useDeleteItem();
  const { mutate: toggleSoldOut } = useToggleSoldOut();

  const items: Item[] = data?.items ?? [];
  const total = data?.total ?? 0;

  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handlePageChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handleCreate = () => {
    setEditingItem(null);
    setIsDrawerOpen(true);
  };

  const handleEdit = (item: Item) => {
    setEditingItem(item);
    setIsDrawerOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTargetId) return;
    deleteMutate(deleteTargetId, {
      onSuccess: () => setDeleteTargetId(null),
      onError: () => setDeleteTargetId(null),
    });
  };

  const drawerStyles = {
    body: { padding: 0, background: isDarkMode ? '#1a202c' : '#ffffff' },
    header: {
      background: isDarkMode ? '#1a202c' : '#ffffff',
      borderBottom: `1px solid ${isDarkMode ? '#434343' : '#f0f0f0'}`,
      color: isDarkMode ? '#e0e0e0' : 'inherit',
    },
  };

  const previewModalStyles = {
    body: { padding: 8, display: 'flex', justifyContent: 'center', alignItems: 'center' },
  };

  const columns: ColumnsType<Item> = [
    {
      title: 'ITEM NAME',
      key: 'item',
      fixed: 'left',
      width: 240,
      render: (_, record) => (
        <ItemNameCell>
          <ItemThumbnailWrap>
            {record.picture ? (
              <>
                <ThumbnailImg src={record.picture} alt={record.item} />
                <ItemEyeBadge onClick={(e) => { e.stopPropagation(); setPreviewItem(record); }}>
                  <FontAwesomeIcon icon={faEye} fontSize={9} color="#fff" />
                </ItemEyeBadge>
              </>
            ) : (
              <NoImagePlaceholder>
                <FontAwesomeIcon icon={faImage} fontSize={18} />
              </NoImagePlaceholder>
            )}
            {record.badgeImage && (
              <BadgeOverlay src={record.badgeImage} alt="badge" />
            )}
          </ItemThumbnailWrap>
          <ItemNameText>{record.item}</ItemNameText>
        </ItemNameCell>
      ),
    },
    {
      title: 'DESCRIPTION',
      dataIndex: 'description',
      key: 'description',
      width: 260,
      render: (desc: string) => <DescriptionText>{desc || '—'}</DescriptionText>,
    },
    {
      title: 'PRICE',
      dataIndex: 'price',
      key: 'price',
      width: 100,
      align: 'center',
      render: (price: number) => (
        <StatusTag $variant="price" $isDark={isDarkMode}>
          ${price}
        </StatusTag>
      ),
    },
    {
      title: 'TAX',
      key: 'taxMode',
      width: 130,
      align: 'center',
      render: (_, record) => {
        const mode = record.taxMode ?? 'store';
        const label =
          mode === 'non_taxable'
            ? 'Non-taxable'
            : mode === 'custom'
            ? `Custom${record.taxRate ? ` (${(Number(record.taxRate) * 100).toFixed(3).replace(/\.?0+$/, '')}%)` : ''}`
            : 'Store rate';
        return (
          <StatusTag $variant={mode === 'non_taxable' ? 'no' : 'yes-green'} $isDark={isDarkMode}>
            {label}
          </StatusTag>
        );
      },
    },
    {
      title: 'MEAL',
      dataIndex: 'is_make_it_a_meal',
      key: 'is_make_it_a_meal',
      width: 80,
      align: 'center',
      render: (val: boolean) => (
        <StatusTag $variant={val ? 'yes-blue' : 'no'} $isDark={isDarkMode}>
          {val ? 'Yes' : 'No'}
        </StatusTag>
      ),
    },
    {
      title: 'SOLD OUT',
      key: 'sold_out',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const isThis = togglingId === record.id;
        const isBusy = togglingId !== null && !isThis;
        return (
          <SoldOutSwitchWrap $busy={isBusy}>
            <SoldOutSwitch
              $soldOut={record.sold_out}
              checked={record.sold_out}
              loading={isThis}
              onChange={(checked) => {
                setTogglingId(record.id);
                toggleSoldOut(
                  { id: record.id, sold_out: checked, picture: record.picture, badgeImage: record.badgeImage },
                  { onSettled: () => setTogglingId(null) }
                );
              }}
              checkedChildren="Sold"
              unCheckedChildren="On"
              disabled={isBusy}
            />
          </SoldOutSwitchWrap>
        );
      },
    },
    ...(role !== 'staff'
      ? [
          {
            title: 'ACTIONS',
            key: 'actions',
            width: 100,
            align: 'center' as const,
            fixed: 'right' as const,
            render: (_: any, record: Item) => (
              <ActionsCell>
                <Tooltip title="Edit">
                  <ActionIconButton iconColor="#2D6CCA" onClick={() => handleEdit(record)}>
                    <FontAwesomeIcon icon={faPen} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                  </ActionIconButton>
                </Tooltip>
                <Tooltip title="Delete">
                  <ActionIconButton
                    iconColor="#f5222d"
                    onClick={(e) => { e.stopPropagation(); setDeleteTargetId(record.id); }}
                  >
                    <FontAwesomeIcon icon={faTrash} fontSize={13} color={isDarkMode ? '#fff' : '#222222'} />
                  </ActionIconButton>
                </Tooltip>
              </ActionsCell>
            ),
          },
        ]
      : []),
  ];

  return (
    <ConfigProvider theme={getAntdThemeConfig(isDarkMode)}>
    <PageWrapper>
      {role !== 'staff' && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <AddButton type="primary" icon={<FontAwesomeIcon icon={faPlus} />} onClick={handleCreate} size="large">Add New Item</AddButton>
        </div>
      )}

      <CustomInput
        placeholder="Search items by name..."
        value={searchQuery}
        onChange={handleSearch}
        allowClear
        width="300px"
      />

      <TableWrapper>
        <Table<Item>
          columns={columns}
          dataSource={items}
          rowKey="id"
          loading={isLoading || isFetching}
          pagination={false}
          scroll={items.length > 0 ? { x: true, y: 600 } : undefined}
          disableScroll={items.length === 0}
          emptyText="No items found"
          headerBackgroundColor={isDarkMode ? '#1a202c' : '#F2F2F2'}
          removeVerticalBorders
          thPadding="15px 10px"
          tdPadding="10px 10px"
          firstChildBorderRadius="8px"
          border="0"
          borderBottomLeftRight
          theme={isDarkMode ? 'dark' : 'light'}
        />
      </TableWrapper>

      {total > 0 && (
        <TablePagination
          totalCount={total}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePageChange}
          totalLabel="Total items"
          pageSizeOptions={[10, 20, 50]}
          padding="16px 12px 0px 12px"
        />
      )}

      <Drawer
        title={editingItem ? 'Edit Item' : 'Add New Item'}
        placement="right"
        onClose={() => setIsDrawerOpen(false)}
        maskClosable={false}
        open={isDrawerOpen}
        width={760}
        destroyOnClose
        styles={drawerStyles}
      >
        <DrawerContentWrapper>
          <ItemFormModal
            open={isDrawerOpen}
            onClose={() => setIsDrawerOpen(false)}
            editingItem={editingItem}
            onSuccess={() => setIsDrawerOpen(false)}
          />
        </DrawerContentWrapper>
      </Drawer>

      <Modal
        open={!!deleteTargetId}
        onCancel={() => setDeleteTargetId(null)}
        onOk={confirmDelete}
        confirmLoading={isDeleting}
        title="Delete Item"
        okText="Yes, Delete"
        okButtonProps={{ danger: true }}
        centered
      >
        <Text>Are you sure you want to delete this item? This action cannot be undone.</Text>
      </Modal>

      <Modal
        open={!!previewItem}
        onCancel={() => setPreviewItem(null)}
        centered
        width="auto"
        title={previewItem?.item}
        footer={null}
        styles={previewModalStyles}
      >
        <PreviewImageWrapper>
          <PreviewImg src={previewItem?.picture || ''} alt={previewItem?.item} />
          {previewItem?.badgeImage && (
            <PreviewBadgeOverlay src={previewItem.badgeImage} alt="badge" />
          )}
        </PreviewImageWrapper>
      </Modal>
    </PageWrapper>
    </ConfigProvider>
  );
};

export default Items;
